/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as NagbarActionCreators from '@app/actions/NagbarActionCreators';
import * as PremiumModalActionCreators from '@app/actions/PremiumModalActionCreators';
import {usePremiumData} from '@app/components/modals/components/plutonium/hooks/usePremiumData';
import {Nagbar} from '@app/components/layout/Nagbar';
import {NagbarButton} from '@app/components/layout/NagbarButton';
import {NagbarContent} from '@app/components/layout/NagbarContent';
import GeoIPStore from '@app/stores/GeoIPStore';
import UserStore from '@app/stores/UserStore';
import {MS_PER_DAY} from '@fluxer/date_utils/src/DateConstants';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

function useTrialExpiringMessage(): React.ReactNode {
	const user = UserStore.currentUser;
	const countryCode = GeoIPStore.countryCode;
	const {monthlyPrice, yearlyPrice} = usePremiumData(countryCode);

	return useMemo(() => {
		const trialEndsAt = user?.premiumOutOfBandTrialEndsAt;
		if (!trialEndsAt) {
			return (
				<Trans>
					Hey, your Plutonium trial is expiring soon. If you like it, you can keep going for just {monthlyPrice}/mo or{' '}
					{yearlyPrice}/yr. Or you can just not. I won't tell!
				</Trans>
			);
		}

		const msRemaining = trialEndsAt.getTime() - Date.now();
		const daysRemaining = Math.ceil(msRemaining / MS_PER_DAY);

		if (daysRemaining >= 2) {
			return (
				<Trans>
					Hey, your Plutonium trial expires in {daysRemaining} days. If you like it, you can keep going for just{' '}
					{monthlyPrice}/mo or {yearlyPrice}/yr. Or you can just not. I won't tell!
				</Trans>
			);
		}

		if (daysRemaining === 1) {
			return (
				<Trans>
					Hey, your Plutonium trial expires in about a day. If you like it, you can keep going for just {monthlyPrice}
					/mo or {yearlyPrice}/yr. Or you can just not. I won't tell!
				</Trans>
			);
		}

		return (
			<Trans>
				Hey, your Plutonium trial expires in less than a day. If you like it, you can keep going for just {monthlyPrice}
				/mo or {yearlyPrice}/yr. Or you can just not. I won't tell!
			</Trans>
		);
	}, [user?.premiumOutOfBandTrialEndsAt, monthlyPrice, yearlyPrice]);
}

export const PremiumTrialExpiringNagbar = observer(function PremiumTrialExpiringNagbar({
	isMobile,
}: {
	isMobile: boolean;
}) {
	const message = useTrialExpiringMessage();

	const handleUpgrade = useCallback(() => {
		NagbarActionCreators.dismissNagbar('premiumTrialExpiringDismissed');
		PremiumModalActionCreators.open();
	}, []);

	const handleDismiss = useCallback(() => {
		NagbarActionCreators.dismissNagbar('premiumTrialExpiringDismissed');
	}, []);

	return (
		<Nagbar isMobile={isMobile} backgroundColor="#f97316" textColor="#ffffff" dismissible onDismiss={handleDismiss}>
			<NagbarContent
				isMobile={isMobile}
				onDismiss={handleDismiss}
				message={message}
				actions={
					<NagbarButton isMobile={isMobile} onClick={handleUpgrade}>
						<Trans>Upgrade</Trans>
					</NagbarButton>
				}
			/>
		</Nagbar>
	);
});
